import { BarTooltipProps } from '@nivo/bar';
import { ResponsiveBar } from '@nivo/bar';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { getCalibrationLevelUIDetails } from 'libs/end-to-end/src/ui';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { determineTestsForFocusAreaSummaryConsideringFilters } from 'libs/end-to-end/src/tests-utils';
import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { getTestCountsByBiasTypeAndDimension } from 'libs/end-to-end/src/tests-utils';
import { TestSummary } from 'libs/end-to-end/src/tests-utils';
import React, { createContext, useContext, useMemo } from 'react';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { CalibrationLevelPill } from 'apps/sigma2-ui/src/components/utils/CalibrationLevelPill';
import { getBarChartAxisLeft } from 'apps/sigma2-ui/src/copilot/components/OptimizationLevelBarChart';
import { DefaultCalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { adjustCalibrationScoreForGraph } from 'apps/sigma2-ui/src/lib/graph-helpers';

const EmptyTestResults = {
  passing: 0,
  incomplete: 0,
  failing: 0,
  total: 0,
};

type BarChartDatum = {
  label: string;
  barLink: string;
  calibrationScore?: number;
  passingTests?: number;
  incompleteTests?: number;
  totalTests?: number;
};

const BarChartTooltip = ({
  datum,
}: {
  datum: BarTooltipProps<BarChartDatum>;
}) => {
  const dimension = datum;
  const language = useLanguage();

  const { calibrationScore, passingTests, totalTests } = dimension.data;
  const context = useContext(FocusAreaSummaryBarChartContext);

  const calibrationLevel = getCalibrationLevel(calibrationScore ?? 0);

  return (
    <div className="filter shadow-8px bg-white text-left p-4 font-sans bg-opacity-70 backdrop-blur-sm rounded-md">
      <p className="text-xl font-bold font-sans">{dimension.data.label}</p>
      <CalibrationLevelPill
        calibrationLevel={calibrationLevel}
        calibrationFraming={context.calibrationFraming}
      />

      <p>
        {language.polarChartTooltipTestStatement({
          passingTests: passingTests ?? 0,
          totalTests: totalTests ?? 0,
        })}
      </p>
    </div>
  );
};

interface FocusAreaSummaryBarChartContext {
  calibrationFraming: CalibrationFraming;
}

export const FocusAreaSummaryBarChartContext =
  createContext<FocusAreaSummaryBarChartContext>({
    calibrationFraming: DefaultCalibrationFraming,
  });

export const FocusAreaSummaryBarChart = ({
  testSummary,
  dimensions,
  focusArea,
  metric,
  calibrationFraming,
}: {
  testSummary: TestSummary | null;
  dimensions: DimensionDefinition[] | null;
  focusArea: string;
  metric: string | null;
  calibrationFraming: CalibrationFraming;
}) => {
  const navigate = useNavigateSafe();
  const language = useLanguage();

  const tests = useMemo(
    () =>
      determineTestsForFocusAreaSummaryConsideringFilters({
        focusArea,
        metric,
        testSummary,
      }),
    [testSummary, focusArea, metric],
  );

  const data = useMemo(() => {
    const dataPoints: BarChartDatum[] = [];

    const allTestResults = getTestCountsByBiasTypeAndDimension(tests);
    for (const dimension of dimensions ?? []) {
      const informationResults =
        allTestResults['Information'].dimensions[
          dimension.columnHeaderValue.toLowerCase()
        ] || EmptyTestResults;
      const structuralResults =
        allTestResults['Structural'].dimensions[
          dimension.columnHeaderValue.toLowerCase()
        ] || EmptyTestResults;
      const preferenceResults =
        allTestResults['Preference'].dimensions[
          dimension.columnHeaderValue.toLowerCase()
        ] || EmptyTestResults;

      const testResults = {
        passing:
          informationResults.passing +
          structuralResults.passing +
          preferenceResults.passing,
        incomplete:
          informationResults.incomplete +
          structuralResults.incomplete +
          preferenceResults.incomplete,
        failing:
          informationResults.failing +
          structuralResults.failing +
          preferenceResults.failing,
      };

      const calibrationScore = getCalibrationScore({
        testCount: testResults,
      });

      dataPoints.push({
        label: dimension.displayName,
        barLink: `/${focusArea}/summary-v2?dimension=${encodeURIComponent(
          dimension.columnHeaderValue,
        )}`,
        calibrationScore: calibrationScore,
        [`${dimension.displayName}`]: calibrationScore,
        passingTests: testResults.passing,
        incompleteTests: testResults.incomplete,
        totalTests: getTotalTestCount(testResults),
      });
    }

    return dataPoints;
  }, [dimensions, focusArea, tests]);

  const keys = useMemo(() => {
    return dimensions?.map((dimension) => dimension.displayName) ?? [];
  }, [dimensions]);

  const barChartColors: string[] = useMemo(() => {
    return (
      data.map((result) => {
        const calibrationScore = result.calibrationScore;
        const calibrationLevel = getCalibrationLevel(calibrationScore ?? 0);
        const uiDetails =
          getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel];
        return uiDetails.hexColor;
      }) || []
    );
  }, [data, calibrationFraming]);

  return (
    <div className="aspect-square relative w-full h-full">
      <FocusAreaSummaryBarChartContext.Provider value={{ calibrationFraming }}>
        <ResponsiveBar
          data={data.map((datum) => ({
            ...datum,
            [datum.label]: adjustCalibrationScoreForGraph(
              datum.calibrationScore ?? 0,
              calibrationFraming,
            ),
          }))}
          groupMode="stacked"
          margin={{ top: 10, right: 50, bottom: 100, left: 100 }}
          minValue={0}
          maxValue={6}
          borderRadius={3}
          colors={barChartColors}
          colorBy="id"
          label=""
          padding={0.4}
          innerPadding={3}
          keys={keys}
          indexBy={'label'}
          enableGridX={false}
          axisLeft={getBarChartAxisLeft({ language, calibrationFraming })}
          tooltip={(datum) => <BarChartTooltip datum={datum} />}
          axisBottom={{
            tickPadding: 8,
            renderTick: (tick) => {
              return (
                <g transform={`translate(${tick.x},${tick.y + 22})`}>
                  <text
                    x={-25}
                    y={15}
                    textAnchor="before"
                    transform="rotate(25)"
                    style={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      color: '#3B4046',
                    }}
                  >
                    {tick.value}
                  </text>
                </g>
              );
            },
          }}
          onClick={(datum) => {
            return navigate(datum.data.barLink);
          }}
          theme={{
            text: {
              fontFamily: KARLA_FONT_FAMILY,
              fontSize: 16,
            },
            axis: {
              domain: {
                line: {
                  stroke: '#B8BABC',
                  strokeWidth: 2,
                },
              },
              ticks: {
                line: {
                  strokeWidth: 0,
                },
              },
              legend: {
                text: {
                  stroke: '#791fd1',
                  fontSize: 16,
                  strokeWidth: 0.2,
                },
              },
            },
          }}
        />
      </FocusAreaSummaryBarChartContext.Provider>
    </div>
  );
};
